import { z } from 'zod'

import { planCodeSchema } from './enums'

/**
 * The `details` payload each error code carries (§5.2).
 *
 * `errors.ts` owns the closed code set and its HTTP statuses; this file says
 * what rides along with the ones that carry structured data, so a client can
 * render the right thing without parsing a message string.
 */

/** `VALIDATION_FAILED` · one array of messages per field path, as zod produces. */
export const validationDetailsSchema = z.object({
  fieldErrors: z.record(z.string(), z.array(z.string())),
  formErrors: z.array(z.string()).optional(),
})

/** `ACCOUNT_LOCKED` and `RATE_LIMITED` · what the countdown counts down. */
export const retryAfterDetailsSchema = z.object({
  retryAfterSeconds: z.number().int().nonnegative(),
})

/**
 * `PLAN_LIMIT_EXCEEDED` · everything the upgrade prompt needs, **in the error
 * itself**. The design shows "18 of 20 animals — the Trader plan allows 100",
 * and making the client fetch the plan and the usage after a failed create is
 * two round trips to explain one refusal.
 */
export const planLimitDetailsSchema = z.object({
  /** The quota that was hit. */
  limit: z.number().int().nonnegative(),
  /** Where they stand now. */
  current: z.number().int().nonnegative(),
  planCode: planCodeSchema,
  /** The smallest plan that would fit. `null` when they are already on the top. */
  suggestedPlanCode: planCodeSchema.nullable(),
  /** Which quota: animals, users or storage. */
  resource: z.enum(['animals', 'users', 'storage']).optional(),
})

/** `PAYLOAD_TOO_LARGE` · named so the message can be about *this* file. */
export const payloadTooLargeDetailsSchema = z.object({
  maxBytes: z.number().int().positive(),
  actualBytes: z.number().int().positive().optional(),
  fileName: z.string().optional(),
})

/**
 * `CONFLICT` · which conflict. Duplicate tag, over-payment, already sold, a
 * stale `If-Match`, shares that do not total 10000, a downgrade blocked by
 * current usage — one code, and this tells the client which dialog to show.
 */
export const CONFLICT_REASONS = [
  'DUPLICATE_TAG',
  'ALREADY_SOLD',
  'OVERPAYMENT',
  'STALE_VERSION',
  'SHARES_NOT_10000',
  'DOWNGRADE_BLOCKED',
  'ALREADY_POSTED',
  'DUPLICATE_USERNAME',
  'DUPLICATE_SLUG',
  /** A plan cannot be deleted while books are subscribed to it. */
  'PLAN_IN_USE',
] as const

export const conflictDetailsSchema = z.object({
  reason: z.enum(CONFLICT_REASONS),
  field: z.string().optional(),
  /** For `STALE_VERSION`: what the row is actually at now. */
  currentVersion: z.number().int().nonnegative().optional(),
  /** For `DOWNGRADE_BLOCKED`: what is in the way. */
  blocking: z
    .array(z.object({ resource: z.string(), current: z.number(), limit: z.number() }))
    .optional(),
  /**
   * For `PLAN_IN_USE`: how many books are on the plan.
   *
   * The count is in the refusal because the console has to say it — "4
   * businesses are on this plan" — and making it fetch the plans list again
   * after a failed delete is a round trip to explain one refusal, exactly as
   * `planLimitDetailsSchema` avoids.
   */
  businesses: z.number().int().nonnegative().optional(),
})

/** `PRICE_LOCKED` · so the PIN sheet can say how long an unlock will last. */
export const priceLockedDetailsSchema = z.object({
  autoLockSeconds: z.number().int().positive(),
  pinSet: z.boolean(),
})

/** `MAINTENANCE_MODE` · the platform's own message, shown full-screen. */
export const maintenanceDetailsSchema = z.object({
  message: z.string(),
  until: z.string().optional(),
})

export type ValidationDetails = z.infer<typeof validationDetailsSchema>
export type RetryAfterDetails = z.infer<typeof retryAfterDetailsSchema>
export type PlanLimitDetails = z.infer<typeof planLimitDetailsSchema>
export type PayloadTooLargeDetails = z.infer<typeof payloadTooLargeDetailsSchema>
export type ConflictDetails = z.infer<typeof conflictDetailsSchema>
export type PriceLockedDetails = z.infer<typeof priceLockedDetailsSchema>
